import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import SessionExpiredModal from './components/SessionExpiredModal/SessionExpiredModal';

export const AuthContext = createContext();

const authChannel = new BroadcastChannel('auth_channel');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const verificationRef = useRef(false);
  const isLoggingOutRef = useRef(false);

  // 1. PRIMERO: Funciones básicas que no dependen de otras
  const clearAuthData = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenTimestamp');
    localStorage.removeItem('autoImportLink');
    setUser(null);
  }, []);

  // 2. SEGUNDO: logout (depende de clearAuthData)
  const logout = useCallback(async (isInactivityLogout = false) => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    try {
      await fetch('https://apiprov.conduto.com.ec/api/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error durante logout:', error);
    } finally {
      clearAuthData();
      authChannel.postMessage({
        type: isInactivityLogout ? 'SESSION_EXPIRED' : 'LOGOUT'
      });

      if (isInactivityLogout) {
        window.location.href = `${process.env.PUBLIC_URL}/login?session=expired`;
      } else {
        window.location.href = `${process.env.PUBLIC_URL}/login`;
      }
    }
  }, [clearAuthData]);

  // 3. TERCERO: verifySession (depende de clearAuthData)
  const verifySession = useCallback(async () => {
    if (verificationRef.current || isLoggingOutRef.current) return false;

    const urlParams = new URLSearchParams(window.location.search);
    if (window.location.pathname.includes('/login') && urlParams.get('session') === 'expired') {
      return false;
    }

    verificationRef.current = true;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const res = await fetch('https://apiprov.conduto.com.ec/api/verify', {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        verificationRef.current = false;
        return false;
      }

      const data = await res.json();

      if (data.usuario) {
        setUser(data.usuario);
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
        }
        localStorage.setItem('userId', data.usuario.id_usuario);
        localStorage.setItem('userRole', data.usuario.rol);
        localStorage.setItem('userData', JSON.stringify(data.usuario));
        localStorage.setItem('tokenTimestamp', Date.now().toString());
        verificationRef.current = false;
        return true;
      }

      clearAuthData();
      verificationRef.current = false;
      return false;

    } catch (error) {
      console.error('Error en verifySession:', error);
      verificationRef.current = false;
      return false;
    }
  }, [clearAuthData]);

  // 4. CUARTO: refreshUser (depende de verifySession)
  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch('https://apiprov.conduto.com.ec/api/verify', {
        method: 'GET',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        if (data.usuario) {
          setUser(data.usuario);
          localStorage.setItem('userData', JSON.stringify(data.usuario));
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error refrescando usuario:', error);
      return false;
    }
  }, []);

  // 5. QUINTO: tryRefreshFromModal (depende de verifySession y logout)
  const tryRefreshFromModal = useCallback(async () => {
    try {
      const res = await fetch('https://apiprov.conduto.com.ec/api/refresh', {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) {
        await logout(true);
        return;
      }

      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('tokenTimestamp', Date.now().toString());
      }

      try {
        localStorage.removeItem('redirectAfterLogin');
      } catch (e) { }

      setShowSessionExpired(false);
      await verifySession();
    } catch (err) {
      console.error('Error refrescando desde modal:', err);
      await logout(true);
    }
  }, [verifySession, logout]);

  // 6. SEXTO: login (no depende de otras funciones)
  const login = useCallback((userData) => {
    localStorage.setItem('userId', userData.id_usuario);
    localStorage.setItem('userRole', userData.rol);
    localStorage.setItem('userData', JSON.stringify(userData));
    localStorage.setItem('tokenTimestamp', Date.now().toString());

    setUser(userData);
    authChannel.postMessage({ type: 'LOGIN' });
  }, []);

  // 7. SÉPTIMO: useEffect para inicialización
  useEffect(() => {
    const initializeAuth = async () => {
      await verifySession();
      setLoading(false);
    };

    initializeAuth();

    const handleSessionExpired = () => {
      if (!isLoggingOutRef.current) {
        try {
          const current = window.location.pathname + window.location.search;
          localStorage.setItem('redirectAfterLogin', current);
        } catch (e) {
          console.error('Error guardando redirect:', e);
        }
        setShowSessionExpired(true);
      }
    };

    const handleMessage = (event) => {
      switch (event.data?.type) {
        case 'LOGOUT':
          isLoggingOutRef.current = true;
          clearAuthData();
          window.location.href = `${process.env.PUBLIC_URL}/login`;
          break;

        case 'LOGIN':
          verifySession();
          break;

        case 'SESSION_EXPIRED':
          isLoggingOutRef.current = true;
          clearAuthData();
          window.location.href = `${process.env.PUBLIC_URL}/login?session=expired`;
          break;

        default:
          break;
      }
    };

    window.addEventListener('session-expired', handleSessionExpired);
    authChannel.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
      authChannel.removeEventListener('message', handleMessage);
    };
  }, [verifySession, clearAuthData]);

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    verifySession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiredModal
        open={showSessionExpired}
        onClose={() => setShowSessionExpired(false)}
        onTryRefresh={tryRefreshFromModal}
        onGoToLogin={() => logout(true)}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};